import AuthServices from '@/services/auth'
import BiometricServices from '@/services/biometrics'
import { getYearsSince } from '@/utils/date'
import childStatus from '@/config/childStatus'
import Vue from 'vue'
import { authClientInstance } from '@/auth/AuthClient'
import moment from 'moment'
import status from '@/config/status'
import clone from 'lodash/clone'

const MAX_PASSES_AGE = process.env.VUE_APP_MAX_PASSES_AGE || 12

const state = () => ({
  user: null,
  authUserInfo: {},
  biometricStatus: null
})

const getters = {
  user: state => state.user,
  biometricStatus: state => state.biometricStatus,
  hasMembers: state => state.user && state.user.familia && state.user.familia.length,
  image: (state, getters, rootState, rootGetters) => {
    return state.user && state.user.no ? rootGetters['images/image'](state.user.no) : null
  },
  firstLetterName: state => state.user && state.user.nombre ? state.user.nombre.charAt(0) : null,
  membership: state => state.user ? state.user.num_socio : null,
  internalNumber: state => state.user ? state.user.no : null,
  years: state => state.user ? getYearsSince(state.user.fecha_nacimiento) : null,
  acceptanceDate: state => {
    const date = state.user?.fecha_aceptacion_politica_privacidad
    return date && moment(date).isAfter('1000-01-01') ? date : null
  },
  revocationDate: state => {
    const date = state.user?.fecha_revocacion_politica_privacidad
    return date && moment(date).isAfter('1000-01-01') ? date : null
  },
  renewalWarning: (state, getters) => {
    if (getters.acceptanceDate && (!getters.revocationDate || (moment(getters.acceptanceDate).isAfter(getters.revocationDate)))) {
      if (getters.years >= 3 && getters.years < 10) {
        return getYearsSince(getters.acceptanceDate) >= 1
      }

      if (getters.years >= 10 && getters.years < 18) {
        return getYearsSince(getters.acceptanceDate) >= 2
      }
    }

    return false
  },
  isMinor: state => state.user ? getYearsSince(state.user.fecha_nacimiento) < 14 : false,
  minors: state => {
    if (state.user?.familia) {
      return state.user.familia.filter(member => getYearsSince(member.fecha_nacimiento) < 14)
    }
    return []
  },
  notifications: (state, getters, rootState, rootGetters) => {
    let notifications = []
    if (state.user) {
      if (getters.renewalWarning) {
        notifications = notifications.concat({
          name: 'Acceso biométrico',
          icon: 'warning',
          description: 'Recomendamos renovar la fotografía de acceso'
        })
      }
      if (getters.biometricStatus === status.ERROR) {
        notifications.push({
          name: 'Acceso biométrico',
          icon: 'error',
          description: 'Error en el proceso de la fotografía para el acceso biométrico'
        })
      }
      const authorizations = state.user.familia
        .filter(member => {
          return member.childBiometricStatus &&
            isPendingBiometricStatus(member.childBiometricStatus.code) &&
            Object.prototype.hasOwnProperty.call(member, state.user.nif_socio) &&
            member[state.user.nif_socio] === false
        })
        .map(member => {
          return {
            name: member.nombre + ' ' + member.apellidos,
            image: member.no ? rootGetters['images/image'](member.no) : null,
            description: 'Autorizar recon. facial',
            membership: member.num_socio
          }
        })

      notifications = notifications.concat(authorizations)
    }
    return notifications
  },
  getAuthUserInfo: state => state.authUserInfo,
  allInternalNumbersMembers: (state, getters) => {
    const internalNumbers = []
    if (state.user?.no) {
      internalNumbers.push(state.user.no)
    }
    if (getters.hasMembers) {
      for (const member of state.user.familia) {
        if (member.no) {
          internalNumbers.push(member.no)
        }
      }
    }
    return internalNumbers
  },
  allMembers: (state, getters) => {
    const members = []
    if (state.user) {
      const user = clone(state.user)
      user.years = getYearsSince(user.fecha_nacimiento)
      delete user.familia
      members.push(user)
    }
    if (getters.hasMembers) {
      for (const member of state.user.familia) {
        member.years = getYearsSince(member.fecha_nacimiento)
        members.push(member)
      }
    }
    return members
  },
  passUsers: (state, getters) => {
    const users = []
    if (getYearsSince(state.user?.fecha_nacimiento) <= MAX_PASSES_AGE) {
      users.push({
        internalNumber: state.user.no,
        membership: state.user.num_socio,
        name: state.user.nombre,
        surname: state.user.apellidos,
        years: getYearsSince(state.user.fecha_nacimiento)
      })
    }
    if (getters.hasMembers) {
      for (const member of state.user.familia) {
        if (getYearsSince(member.fecha_nacimiento) <= MAX_PASSES_AGE) {
          users.push({
            internalNumber: member.no,
            membership: member.num_socio,
            name: member.nombre,
            surname: member.apellidos,
            years: getYearsSince(member.fecha_nacimiento)
          })
        }
      }
    }
    return users
  }
}

const mutations = {
  setUser (state, user) {
    state.user = user
  },
  setBiometricStatus (state, status) {
    state.biometricStatus = status
  },
  setChildBiometricStatus (state, { membership, status }) {
    const index = state.user.familia.findIndex(member => member.num_socio === membership)
    if (index !== -1) {
      state.user.familia[index].childBiometricStatus = status
      if (status?.nifParent1) {
        state.user.familia[index][status.nifParent1] = state.user.familia[index].aprobacion_progenitor1
      }
      if (status?.nifParent2) {
        state.user.familia[index][status.nifParent2] = state.user.familia[index].aprobacion_progenitor2
      }
      state.user.familia = [...state.user.familia]
    }
  },
  approveChild: function (state, membership) {
    const index = state.user.familia.findIndex(member => member.num_socio === membership)
    if (index !== -1) {
      const parentNif = state.user.nif_socio
      state.user.familia[index][parentNif] = true
      if (state.user.familia[index].childBiometricStatus.nifParent1 === parentNif) {
        state.user.familia[index].aprobacion_progenitor1 = true
      }
      if (state.user.familia[index].childBiometricStatus.nifParent2 === parentNif) {
        state.user.familia[index].aprobacion_progenitor2 = true
      }

      Vue.set(state.user, 'familia', [...state.user.familia])
    }
  },
  removeUser (state) {
    state.user = null
  },
  setAuthUserInfo: function (state, authUserInfo) {
    state.authUserInfo = authUserInfo
  }
}

const actions = {
  async downloadUserInfo ({ commit, dispatch, getters }) {
    if (!getters.user) {
      await dispatch('downloadMe')
    }
    const user = getters.user
    if (user.cuota !== 'no socio') {
      await dispatch('downloadAddtionalData')
      await dispatch('downloadImages')
      await dispatch('downloadBiometricStatus')
    }
  },
  async downloadMe ({ commit }) {
    const user = await AuthServices.me()
    commit('setUser', user)
  },
  async downloadAddtionalData ({ commit, dispatch, getters }) {
    if (getters.hasMembers) {
      const members = getters.user.familia
      for (const member of members) {
        if (getYearsSince(member.fecha_nacimiento) < 14) {
          const membership = member.num_socio
          const status = await BiometricServices.childStatus(membership)
          commit('setChildBiometricStatus', { membership, status })
        }
      }
    }
  },
  async downloadImages ({ commit, dispatch, getters }) {
    const internalNumbers = getters.allInternalNumbersMembers
    for (const internalNumber of internalNumbers) {
      dispatch('images/downloadImage', internalNumber, { root: true })
    }
  },
  async downloadBiometricStatus ({ commit, getters }) {
    const status = await BiometricServices.status(getters.membership)
    commit('setBiometricStatus', status)
  },
  async logout ({ commit }) {
    commit('removeUser')
    commit('images/removeImages', null, { root: true })
    await authClientInstance.logout()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

function isPendingBiometricStatus (code) {
  const pendingBiometricStatus = [
    childStatus.PENDING_BOTH_PARENTS,
    childStatus.APPROVED_ONLY_PARENT_1,
    childStatus.APPROVED_ONLY_PARENT_2
  ]

  return pendingBiometricStatus.includes(code)
}
